import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { SpecraBadge, SpecraButton, SpecraHeading, SpecraText, specraButtonVariants } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { projectDataClient } from '../../../lib/connect/clients';
import { Choice, options, TextField } from '../PastPerformance/Fields';
const T = Testids.captureStaffingReadiness;
const teammateStatuses = [
    'proposed',
    'unconfirmed'
];
const hiringStatuses = [
    'planned',
    'in_progress',
    'ready',
    'blocked',
    'cancelled'
];
function draftFrom(current) {
    return {
        kind: current?.kind ?? 'hiring',
        candidateId: current?.candidateId ?? '',
        workshareId: current?.workshareId ?? '',
        role: current?.role ?? '',
        status: current?.status ?? 'planned',
        likelySource: current?.likelySource ?? 'unknown',
        targetDate: current?.targetDate ?? '',
        notes: current?.notes ?? ''
    };
}
function GapPlan({ captureProjectId, readOnly, item, position, teaming, reload }) {
    const [draft, setDraft] = useState(()=>draftFrom(item.current));
    const [revision, setRevision] = useState(item.current?.revision ?? BigInt(0));
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [saved, setSaved] = useState(false);
    const inFlight = useRef(false);
    const request = useRef();
    const patch = (change)=>{
        setDraft((previous)=>({
                ...previous,
                ...change
            }));
        setSaved(false);
    };
    const gap = item.gap;
    if (!gap) return null;
    const gapId = gap.id;
    const selectedCandidateId = readOnly ? item.current?.candidateId : draft.candidateId;
    const candidate = position.candidates.find((c)=>c.id === selectedCandidateId);
    const agreements = teaming.agreements.filter((a)=>a.candidateId === selectedCandidateId);
    const seenAreas = new Set();
    const workshares = teaming.workshare.filter((w)=>{
        if (w.candidateId !== selectedCandidateId) return false;
        const latest = !seenAreas.has(w.area);
        seenAreas.add(w.area);
        return latest || w.id === draft.workshareId;
    });
    const isTeammate = draft.kind === 'teammate';
    const candidateStatus = candidate?.status || (selectedCandidateId === item.current?.candidateId ? item.currentCandidateStatus : '');
    async function save() {
        if (readOnly || inFlight.current) return;
        inFlight.current = true;
        setBusy(true);
        setError('');
        setSaved(false);
        const content = {
            captureProjectId,
            gapId,
            expectedRevision: revision,
            ...draft,
            candidateId: isTeammate ? draft.candidateId : '',
            workshareId: isTeammate ? draft.workshareId : ''
        };
        const fingerprint = JSON.stringify({
            ...content,
            expectedRevision: String(revision)
        });
        if (request.current?.fingerprint !== fingerprint) {
            request.current = {
                fingerprint,
                id: crypto.randomUUID()
            };
        }
        let recorded = false;
        try {
            const response = await projectDataClient.saveCaptureStaffingReadiness({
                ...content,
                requestId: request.current.id
            });
            setRevision(response.revision?.revision ?? revision + BigInt(1));
            request.current = undefined;
            recorded = true;
            setSaved(true);
            await reload();
        } catch (cause) {
            const failure = ConnectError.from(cause);
            setError(recorded ? 'The plan was saved, but the latest readiness could not be loaded. Retry loading readiness before making another change.' : failure.code === Code.Aborted ? 'A newer plan was saved. Copy your unsaved notes, then reload the latest plan before saving again.' : failure.code === Code.PermissionDenied ? 'Save not permitted. Your draft is preserved; ask the pursuit owner for edit access.' : `Could not save the plan: ${failure.rawMessage}. Your draft is preserved; try saving again.`);
        } finally{
            inFlight.current = false;
            setBusy(false);
        }
    }
    async function reloadDraft() {
        if (inFlight.current) return;
        inFlight.current = true;
        setBusy(true);
        try {
            const latest = await reload();
            const current = latest.readiness.gaps.find((g)=>g.gap?.id === gapId)?.current;
            setDraft(draftFrom(current));
            setRevision(current?.revision ?? BigInt(0));
            request.current = undefined;
            setError('');
            setSaved(false);
        } catch (cause) {
            setError(`Could not reload the plan: ${ConnectError.from(cause).rawMessage}. Your draft is preserved.`);
        } finally{
            inFlight.current = false;
            setBusy(false);
        }
    }
    return /*#__PURE__*/ _jsxs("section", {
        "aria-label": gap.description,
        "data-testid": T.gap,
        "data-gap-id": gap.id,
        className: "space-y-3 rounded-md border border-border p-3",
        children: [
            /*#__PURE__*/ _jsx(SpecraHeading, {
                as: "h4",
                size: "sm",
                children: gap.description
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-center gap-2",
                children: [
                    /*#__PURE__*/ _jsxs(SpecraBadge, {
                        "data-testid": T.sourceStatus,
                        children: [
                            "Source gap: ",
                            gap.status
                        ]
                    }),
                    (item.uncovered || item.needsReview) && /*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: "warning",
                        "data-testid": T.uncovered,
                        children: "Uncovered"
                    }),
                    (item.provisional || item.current) && /*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: "info",
                        "data-testid": T.provisional,
                        children: "Provisional working plan"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("ul", {
                className: "space-y-1",
                children: gap.evidence.map((e)=>/*#__PURE__*/ _jsx("li", {
                        "data-testid": T.evidence,
                        children: /*#__PURE__*/ _jsxs(SpecraText, {
                            size: "body-ui",
                            children: [
                                e.sourceLabel,
                                e.sourceRef ? ` — ${e.sourceRef}` : ''
                            ]
                        })
                    }, e.id))
            }),
            item.needsReview && /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                role: "alert",
                tone: "warning",
                "data-testid": T.needsReview,
                children: "Needs review: the teammate relationship or workshare changed. Review the previous plan and confirm the next action."
            }),
            candidateStatus && /*#__PURE__*/ _jsxs(SpecraText, {
                as: "p",
                size: "body-ui",
                "data-testid": T.relationship,
                children: [
                    "Current teammate relationship: ",
                    candidateStatus.replace(/_/g, ' ')
                ]
            }),
            (readOnly ? item.current?.kind === 'teammate' : isTeammate) && /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsxs(SpecraText, {
                        as: "p",
                        size: "body-ui",
                        children: [
                            "Teammate: ",
                            candidate?.companyName || 'Previously linked teammate unavailable'
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        "data-testid": T.currentAgreement,
                        className: "space-y-1",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraText, {
                                size: "body-ui",
                                children: "Current agreements"
                            }),
                            agreements.length ? agreements.map((a)=>/*#__PURE__*/ _jsxs("div", {
                                    "data-agreement-id": a.id,
                                    children: [
                                        /*#__PURE__*/ _jsxs(SpecraText, {
                                            size: "body-ui",
                                            children: [
                                                a.agreementType.toUpperCase(),
                                                ": "
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraBadge, {
                                            children: a.state.replace(/_/g, ' ')
                                        })
                                    ]
                                }, a.id)) : /*#__PURE__*/ _jsx(SpecraText, {
                                as: "p",
                                size: "body-ui",
                                children: "No agreement recorded"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(Link, {
                        "data-testid": T.openTeammate,
                        className: specraButtonVariants({
                            variant: 'outline'
                        }),
                        to: `/capture/${encodeURIComponent(captureProjectId)}?tab=overview&outputOwner=pursuit-team`,
                        children: "Open teammate"
                    })
                ]
            }),
            !readOnly && /*#__PURE__*/ _jsxs("fieldset", {
                disabled: busy,
                className: "space-y-3",
                children: [
                    busy && /*#__PURE__*/ _jsx("output", {
                        "data-testid": T.gapLoading,
                        children: /*#__PURE__*/ _jsx(SpecraText, {
                            children: "Updating staffing plan…"
                        })
                    }),
                    /*#__PURE__*/ _jsx(Choice, {
                        label: "Kind",
                        testId: T.kind,
                        value: draft.kind,
                        options: [
                            {
                                value: 'teammate',
                                label: 'Teammate responsibility'
                            },
                            {
                                value: 'hiring',
                                label: 'Hiring action'
                            }
                        ],
                        onChange: (kind)=>patch({
                                kind,
                                candidateId: '',
                                workshareId: '',
                                status: kind === 'teammate' ? 'proposed' : 'planned'
                            })
                    }),
                    isTeammate && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(Choice, {
                                label: "Teammate",
                                testId: T.teammate,
                                value: draft.candidateId,
                                options: [
                                    {
                                        value: '',
                                        label: 'Select a linked teammate…'
                                    },
                                    ...position.candidates.map((c)=>({
                                            value: c.id,
                                            label: c.companyName
                                        }))
                                ],
                                onChange: (candidateId)=>patch({
                                        candidateId,
                                        workshareId: ''
                                    })
                            }),
                            /*#__PURE__*/ _jsx(Choice, {
                                label: "Workshare (optional)",
                                testId: T.workshare,
                                value: draft.workshareId,
                                options: [
                                    {
                                        value: '',
                                        label: 'No workshare reference'
                                    },
                                    ...workshares.map((w)=>({
                                            value: w.id,
                                            label: `${w.area} — ${w.plannedSharePct}% of whole effort`
                                        }))
                                ],
                                onChange: (workshareId)=>patch({
                                        workshareId
                                    })
                            }),
                            /*#__PURE__*/ _jsx(SpecraText, {
                                as: "p",
                                size: "meta",
                                tone: "muted",
                                children: "Workshare percentages describe the whole effort; they do not measure this gap’s coverage."
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(TextField, {
                        label: "Role",
                        testId: T.role,
                        value: draft.role,
                        onChange: (role)=>patch({
                                role
                            })
                    }),
                    /*#__PURE__*/ _jsx(Choice, {
                        label: "Status",
                        testId: T.status,
                        value: draft.status,
                        options: options(isTeammate ? teammateStatuses : hiringStatuses),
                        onChange: (status)=>patch({
                                status
                            })
                    }),
                    /*#__PURE__*/ _jsx(Choice, {
                        label: "Likely source",
                        testId: T.likelySource,
                        value: draft.likelySource,
                        options: options([
                            'internal',
                            'recruiting',
                            'partner',
                            'unknown'
                        ]),
                        onChange: (likelySource)=>patch({
                                likelySource
                            })
                    }),
                    /*#__PURE__*/ _jsx(TextField, {
                        label: "Target date (YYYY-MM-DD)",
                        testId: T.targetDate,
                        value: draft.targetDate,
                        onChange: (targetDate)=>patch({
                                targetDate
                            })
                    }),
                    /*#__PURE__*/ _jsx(TextField, {
                        label: "Notes",
                        multiline: true,
                        testId: T.notes,
                        value: draft.notes,
                        onChange: (notes)=>patch({
                                notes
                            })
                    }),
                    /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        size: "meta",
                        tone: "muted",
                        children: "Include availability uncertainty, remaining gaps, who will follow up, and when they will confirm readiness."
                    }),
                    error && /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        role: "alert",
                        tone: "danger",
                        "data-testid": T.saveError,
                        children: error
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": T.save,
                        isLoading: busy,
                        disabled: busy || !draft.role.trim() || isTeammate && !draft.candidateId,
                        onClick: save,
                        children: "Save plan"
                    }),
                    error && /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": T.reloadDraft,
                        onClick: reloadDraft,
                        children: "Reload latest (discards this draft)"
                    }),
                    saved && /*#__PURE__*/ _jsx("output", {
                        "data-testid": T.saved,
                        children: /*#__PURE__*/ _jsx(SpecraText, {
                            children: "Plan saved"
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                "data-testid": T.history,
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraHeading, {
                        as: "h4",
                        size: "sm",
                        children: "Plan history"
                    }),
                    !item.history.length && /*#__PURE__*/ _jsx(SpecraText, {
                        size: "body-ui",
                        children: "No plan recorded yet"
                    }),
                    /*#__PURE__*/ _jsx("ul", {
                        className: "space-y-2",
                        children: item.history.map((entry)=>{
                            const company = position.candidates.find((c)=>c.id === entry.candidateId);
                            const workshare = teaming.workshare.find((w)=>w.id === entry.workshareId);
                            const sourceEvent = company?.history.find((event)=>event.id === entry.sourceCandidateEventId);
                            const date = entry.createdAt?.toDate();
                            return /*#__PURE__*/ _jsxs("li", {
                                "data-testid": T.historyRow,
                                "data-revision-id": entry.id,
                                className: "space-y-1 rounded-md border border-border p-2",
                                children: [
                                    /*#__PURE__*/ _jsxs(SpecraText, {
                                        as: "p",
                                        size: "body-ui",
                                        children: [
                                            "Version ",
                                            String(entry.revision),
                                            " ·",
                                            ' ',
                                            entry.kind === 'teammate' ? 'Teammate responsibility' : 'Hiring action',
                                            " ·",
                                            ' ',
                                            entry.role
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraBadge, {
                                        children: entry.status.replace(/_/g, ' ')
                                    }),
                                    /*#__PURE__*/ _jsxs(SpecraText, {
                                        as: "p",
                                        size: "body-ui",
                                        children: [
                                            "Likely source: ",
                                            entry.likelySource.replace(/_/g, ' '),
                                            " · Target:",
                                            ' ',
                                            entry.targetDate || 'Not recorded'
                                        ]
                                    }),
                                    entry.kind === 'teammate' && /*#__PURE__*/ _jsxs(SpecraText, {
                                        as: "p",
                                        size: "body-ui",
                                        children: [
                                            "Teammate: ",
                                            company?.companyName || 'Previously linked teammate unavailable',
                                            workshare ? ` · Workshare: ${workshare.area} (${workshare.plannedSharePct}% of whole effort)` : entry.workshareId ? ' · Previous workshare unavailable' : ''
                                        ]
                                    }),
                                    entry.sourceCandidateEventId && /*#__PURE__*/ _jsxs(SpecraText, {
                                        as: "p",
                                        size: "meta",
                                        "data-testid": T.historySource,
                                        "data-source-event-id": entry.sourceCandidateEventId,
                                        children: [
                                            "Relationship when recorded:",
                                            ' ',
                                            sourceEvent ? `${sourceEvent.status.replace(/_/g, ' ')} — ${sourceEvent.note}` : 'Previous relationship details unavailable'
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraText, {
                                        as: "p",
                                        size: "body-ui",
                                        className: "whitespace-pre-wrap",
                                        children: entry.notes || 'No notes recorded'
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraText, {
                                        as: "p",
                                        size: "meta",
                                        tone: "muted",
                                        "data-testid": T.historyActor,
                                        "data-user-id": entry.actorUserId,
                                        children: entry.actorUserId ? `Recorded by user ${entry.actorUserId}` : 'Author not recorded'
                                    }),
                                    date ? /*#__PURE__*/ _jsx(SpecraText, {
                                        as: "p",
                                        size: "meta",
                                        children: /*#__PURE__*/ _jsx("time", {
                                            "data-testid": T.historyDate,
                                            dateTime: date.toISOString(),
                                            children: date.toLocaleString()
                                        })
                                    }) : /*#__PURE__*/ _jsx(SpecraText, {
                                        as: "p",
                                        size: "meta",
                                        children: "Date not recorded"
                                    })
                                ]
                            }, entry.id);
                        })
                    })
                ]
            })
        ]
    });
}
export function StaffingReadiness({ captureProjectId, readOnly = false }) {
    const query = useQuery({
        queryKey: [
            'capture-staffing-readiness',
            captureProjectId
        ],
        queryFn: async ()=>{
            const [readiness, position, teaming] = await Promise.all([
                projectDataClient.getCaptureStaffingReadiness({
                    captureProjectId
                }),
                projectDataClient.getCaptureTeamingPosition({
                    captureProjectId
                }),
                projectDataClient.getCaptureTeamingAgreements({
                    captureProjectId
                })
            ]);
            return {
                readiness,
                position,
                teaming
            };
        },
        retry: false,
        refetchOnMount: 'always'
    });
    async function reload() {
        const result = await query.refetch();
        if (result.error) throw result.error;
        if (!result.data) throw new Error('Staffing readiness data unavailable');
        return result.data;
    }
    if (query.isError) return /*#__PURE__*/ _jsxs("div", {
        "data-testid": T.root,
        className: "space-y-2",
        children: [
            /*#__PURE__*/ _jsxs(SpecraText, {
                as: "p",
                role: "alert",
                tone: "danger",
                "data-testid": T.loadError,
                children: [
                    "Could not load staffing readiness: ",
                    ConnectError.from(query.error).rawMessage
                ]
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                "data-testid": T.reload,
                onClick: ()=>query.refetch(),
                children: "Retry loading readiness"
            })
        ]
    });
    if (query.isPending || !query.isFetchedAfterMount || !query.data) return /*#__PURE__*/ _jsx("output", {
        "data-testid": T.loading,
        children: /*#__PURE__*/ _jsx(SpecraText, {
            children: "Loading staffing readiness…"
        })
    });
    return /*#__PURE__*/ _jsxs("section", {
        "data-testid": T.root,
        className: "space-y-3 rounded-md border border-border p-3",
        children: [
            /*#__PURE__*/ _jsx(SpecraHeading, {
                as: "h3",
                size: "sm",
                children: "Teammate responsibility and hiring readiness"
            }),
            /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                size: "body-ui",
                tone: "muted",
                children: "Working plans remain provisional. Recording readiness does not resolve a source gap, allocate an employee, change LOE, or establish a signed commitment."
            }),
            readOnly && /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                size: "body-ui",
                children: "Read-only staffing plans"
            }),
            !query.data.readiness.gaps.length && /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                size: "body-ui",
                children: "No staffing gaps recorded in this pursuit."
            }),
            query.data.readiness.gaps.map((item)=>/*#__PURE__*/ _jsx(GapPlan, {
                    captureProjectId: captureProjectId,
                    readOnly: readOnly,
                    item: item,
                    position: query.data.position,
                    teaming: query.data.teaming,
                    reload: reload
                }, `${captureProjectId}:${item.gap?.id}`))
        ]
    });
}
